import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Check, CheckCircle, ChevronDown, Clock, History, Loader2, Search, Send, X } from 'lucide-react';
import { api } from '../services/api';

const formatMoney = (value) => `${Math.round(Number(value || 0)).toLocaleString('fr-FR')} FCFA`;

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getEmployeeName = (employee) => {
  if (!employee) return '';
  return [employee.prenom, employee.nom].filter(Boolean).join(' ').trim();
};

const SALARY_MODAL_OVERLAY_CLASS = 'fixed inset-0 z-[12000] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm';

const renderInPortal = (content) => {
  if (typeof document === 'undefined') return content;
  return createPortal(content, document.body);
};

const EffectMonthDropdown = ({ value, months, onChange, disabled = false }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapperRef = useRef(null);

  const selectedMonth = months.find((month) => String(month.id) === String(value));
  const filteredMonths = months.filter((month) =>
    String(month.nom || '').toLowerCase().includes(query.toLowerCase())
  );

  useEffect(() => {
    if (!open) return undefined;

    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setOpen(false);
        setQuery('');
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  return (
    <div className="relative mt-1" ref={wrapperRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          if (!disabled) setOpen((current) => !current);
        }}
        className={`flex min-h-[44px] w-full items-center justify-between gap-3 rounded-lg border bg-white px-4 py-2.5 text-left text-sm font-medium transition-colors focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/20 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400 ${
          selectedMonth ? 'border-[#246990]/30 text-slate-900' : 'border-slate-200 text-slate-400 hover:border-slate-300'
        }`}
      >
        <span className="truncate">
          {selectedMonth?.nom || (months.length > 0 ? 'Choisir un mois' : 'Aucun mois disponible')}
        </span>
        <ChevronDown size={18} className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-[12020] mt-2 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl">
          <div className="border-b border-slate-100 bg-slate-50 p-2">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Rechercher un mois..."
                className="h-10 w-full rounded-md border border-slate-200 bg-white pl-9 pr-3 text-sm outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/15"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-60 overflow-y-auto py-1">
            {filteredMonths.length === 0 ? (
              <div className="px-4 py-4 text-center text-sm text-slate-500">Aucun mois autorise trouve</div>
            ) : (
              filteredMonths.map((month) => {
                const isSelected = String(month.id) === String(value);
                return (
                  <button
                    key={month.id}
                    type="button"
                    onClick={() => {
                      onChange(String(month.id));
                      setOpen(false);
                      setQuery('');
                    }}
                    className={`flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left text-sm transition-colors ${
                      isSelected ? 'bg-[#246990]/10 font-semibold text-[#246990]' : 'text-slate-700 hover:bg-[#eef7fb]'
                    }`}
                  >
                    <span className="truncate">{month.nom}</span>
                    {isSelected && <Check size={16} className="shrink-0 text-[#246990]" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const SalaryModificationRequestModal = ({
  open,
  employee,
  selectedDate,
  anneeActive,
  typePersonnel = 'personnel',
  onClose,
  onSuccess,
  showToast
}) => {
  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newSalary, setNewSalary] = useState('');
  const [effectMonthId, setEffectMonthId] = useState('');
  const [motif, setMotif] = useState('');

  const selectedMonth = selectedDate ? selectedDate.getMonth() + 1 : '';
  const selectedYear = selectedDate ? selectedDate.getFullYear() : '';

  const eligibleMonths = useMemo(() => {
    return (context?.mois_eligibles || []).filter((month) => !month.locked);
  }, [context]);

  const salaryLimit = context?.parametres_salaires || null;
  const salaryLimitReached = Boolean(salaryLimit?.plafond_atteint);

  useEffect(() => {
    if (!open || !employee?.id) return;

    const loadContext = async () => {
      setLoading(true);
      setContext(null);
      setNewSalary('');
      setEffectMonthId('');
      setMotif('');

      try {
        const response = await api.paiementsSalaires.getSalaryModificationContext(employee.id, {
          type_personnel: typePersonnel,
          mois: selectedMonth,
          annee: selectedYear,
          annee_scolaire_id: anneeActive?.id,
          employe_role_id: employee.employe_role_id
        });

        if (!response.success) {
          throw new Error(response.message || response.error || 'Contexte indisponible');
        }

        const data = response.data;
        setContext(data);
        setNewSalary(String(data?.salaire_actuel || employee.salaire_fixe || ''));
        setEffectMonthId(data?.mois_effet_propose?.id ? String(data.mois_effet_propose.id) : '');
      } catch (error) {
        showToast?.(error.message || 'Erreur lors de la preparation de la demande', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadContext();
  }, [open, employee?.id, employee?.employe_role_id, selectedMonth, selectedYear, anneeActive?.id, typePersonnel]);

  if (!open) return null;

  const handleSubmit = async () => {
    if (salaryLimitReached) {
      showToast?.('Plafond annuel de modifications salariales atteint pour cet employe.', 'error');
      return;
    }

    const amount = Math.round(Number(newSalary || 0));
    if (!amount || amount <= 0) {
      showToast?.('Saisissez un nouveau salaire valide.', 'error');
      return;
    }
    if (!effectMonthId) {
      showToast?.('Choisissez le mois d effet.', 'error');
      return;
    }
    if (!motif.trim()) {
      showToast?.('Le motif de modification est obligatoire.', 'error');
      return;
    }

    setSubmitting(true);
    showToast?.('Envoi de la demande de modification salaire...', 'loading');

    try {
      const response = await api.paiementsSalaires.createSalaryModificationRequest({
        employe_id: employee.id,
        type_personnel: typePersonnel,
        nouveau_salaire: amount,
        mois_effet_id: Number(effectMonthId),
        motif: motif.trim(),
        annee_scolaire_id: anneeActive?.id,
        employe_role_id: employee.employe_role_id
      });

      if (!response.success) {
        throw new Error(response.message || response.error || 'Demande refusee');
      }

      showToast?.('Demande envoyee au Centre de Support.', 'success');
      onSuccess?.();
      onClose?.();
    } catch (error) {
      showToast?.(error.message || 'Erreur lors de l envoi de la demande', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return renderInPortal(
    <div className={SALARY_MODAL_OVERLAY_CLASS}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#246990]">Demande sensible</p>
            <h3 className="text-xl font-bold text-slate-900">Modification du salaire fixe</h3>
            <p className="text-sm text-slate-500">{getEmployeeName(employee)}</p>
            {employee?.role_nom || employee?.role ? (
              <p className="mt-1 text-xs font-semibold text-[#246990]">{employee.role_nom || employee.role}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(90vh-132px)] overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[260px] flex-col items-center justify-center text-slate-500">
              <Loader2 className="mb-3 animate-spin text-[#246990]" size={28} />
              <p className="font-medium">Lecture des mois verrouilles...</p>
            </div>
          ) : context?.demande_en_attente ? (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-800">
              <div className="flex items-center gap-2 font-semibold">
                <AlertTriangle size={18} />
                Une demande est deja en attente
              </div>
              <p className="mt-2 text-sm">
                Demande #{context.demande_en_attente.id} creee le {formatDateTime(context.demande_en_attente.date_demande)}.
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                <div className="rounded-lg border border-blue-100 bg-blue-50 p-4">
                  <p className="text-xs font-semibold uppercase text-blue-700">Salaire actuel</p>
                  <p className="mt-2 text-2xl font-bold text-blue-900">{formatMoney(context?.salaire_actuel)}</p>
                </div>
                <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-4">
                  <p className="text-xs font-semibold uppercase text-emerald-700">Premier mois autorise</p>
                  <p className="mt-2 text-lg font-bold text-emerald-900">{context?.premier_mois_autorise?.nom || '-'}</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-xs font-semibold uppercase text-slate-500">Mois verrouilles</p>
                  <p className="mt-2 text-lg font-bold text-slate-900">{context?.mois_verrouilles?.length || 0}</p>
                </div>
                <div className={`rounded-lg border p-4 ${salaryLimitReached ? 'border-red-200 bg-red-50' : 'border-amber-100 bg-amber-50'}`}>
                  <p className={`text-xs font-semibold uppercase ${salaryLimitReached ? 'text-red-700' : 'text-amber-700'}`}>
                    Modifications annee
                  </p>
                  <p className={`mt-2 text-lg font-bold ${salaryLimitReached ? 'text-red-900' : 'text-amber-900'}`}>
                    {salaryLimit?.modifications_approuvees ?? 0} / {salaryLimit?.max_modifications_salaire_fixe ?? '-'}
                  </p>
                  <p className={`mt-1 text-xs ${salaryLimitReached ? 'text-red-700' : 'text-amber-700'}`}>
                    {salaryLimitReached ? 'Plafond atteint' : `${salaryLimit?.modifications_restantes ?? '-'} restante(s)`}
                  </p>
                </div>
              </div>

              {salaryLimitReached && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800">
                  <div className="flex items-center gap-2 font-semibold">
                    <AlertTriangle size={18} />
                    Plafond annuel atteint
                  </div>
                  <p className="mt-2 text-sm">
                    Cet employe a deja atteint le nombre maximum de modifications salariales autorisees pour cette annee scolaire.
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-slate-200 bg-white p-4">
                <p className="text-sm font-semibold text-slate-900">Regle appliquee</p>
                <p className="mt-1 text-sm text-slate-600">
                  Les mois deja payes, clotures ou avec avance ne sont pas modifiables. Le nouveau salaire s applique uniquement a partir du mois d effet choisi.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Nouveau salaire mensuel</span>
                  <div className="mt-1 flex rounded-md border border-slate-300 bg-white focus-within:ring-2 focus-within:ring-[#246990]/20">
                    <input
                      type="number"
                      min="1"
                      value={newSalary}
                      onChange={(event) => setNewSalary(event.target.value)}
                      className="w-full rounded-l-md border-0 px-3 py-2 outline-none"
                    />
                    <span className="rounded-r-md bg-slate-50 px-3 py-2 text-sm font-semibold text-slate-500">FCFA</span>
                  </div>
                </label>

                <label className="block">
                  <span className="text-sm font-medium text-slate-700">Mois d effet</span>
                  <EffectMonthDropdown
                    value={effectMonthId}
                    months={eligibleMonths}
                    onChange={setEffectMonthId}
                    disabled={loading || submitting || eligibleMonths.length === 0}
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-slate-700">Motif de la modification</span>
                <textarea
                  value={motif}
                  onChange={(event) => setMotif(event.target.value)}
                  rows={4}
                  className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 outline-none focus:border-[#246990] focus:ring-2 focus:ring-[#246990]/20"
                  placeholder="Ex: revalorisation contractuelle, changement de poste, decision direction..."
                />
              </label>

              {context?.mois_verrouilles?.length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="mb-2 text-sm font-semibold text-slate-900">Mois deja verrouilles</p>
                  <div className="flex flex-wrap gap-2">
                    {context.mois_verrouilles.map((month) => (
                      <span key={month.id} className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600">
                        {month.nom}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 border-t border-slate-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-200 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-50"
            disabled={submitting}
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading || submitting || context?.demande_en_attente || salaryLimitReached || eligibleMonths.length === 0}
            className="inline-flex items-center gap-2 rounded-md bg-[#246990] px-4 py-2 font-semibold text-white hover:bg-[#1e587b] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
            Envoyer la demande
          </button>
        </div>
      </div>
    </div>
  );
};

export const SalaryModificationHistoryModal = ({
  open,
  employee,
  selectedDate,
  anneeActive,
  onClose,
  showToast
}) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !employee?.id) return;

    const loadHistory = async () => {
      setLoading(true);
      try {
        const response = await api.paiementsSalaires.getSalaryModificationHistory(employee.id, {
          mois: selectedDate ? selectedDate.getMonth() + 1 : '',
          annee: selectedDate ? selectedDate.getFullYear() : '',
          annee_scolaire_id: anneeActive?.id
        });

        if (!response.success) {
          throw new Error(response.message || response.error || 'Historique indisponible');
        }

        setHistory(response.data || []);
      } catch (error) {
        showToast?.(error.message || 'Erreur lors du chargement de l historique', 'error');
      } finally {
        setLoading(false);
      }
    };

    loadHistory();
  }, [open, employee?.id, selectedDate, anneeActive?.id]);

  if (!open) return null;

  return renderInPortal(
    <div className={SALARY_MODAL_OVERLAY_CLASS}>
      <div className="max-h-[90vh] w-full max-w-3xl overflow-hidden rounded-lg bg-white shadow-xl">
        <div className="flex items-start justify-between border-b border-slate-200 px-6 py-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#246990]">Historique</p>
            <h3 className="text-xl font-bold text-slate-900">Modifications de salaire</h3>
            <p className="text-sm text-slate-500">{getEmployeeName(employee)}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-full bg-slate-100 p-2 text-slate-500 hover:bg-slate-200">
            <X size={18} />
          </button>
        </div>

        <div className="max-h-[calc(90vh-90px)] overflow-y-auto px-6 py-5">
          {loading ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center text-slate-500">
              <Loader2 className="mb-3 animate-spin text-[#246990]" size={28} />
              <p className="font-medium">Chargement de l historique...</p>
            </div>
          ) : history.length === 0 ? (
            <div className="flex min-h-[220px] flex-col items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 text-center text-slate-500">
              <History size={34} className="mb-3 text-slate-300" />
              <p className="font-semibold">Aucune modification de salaire</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((item) => (
                <div key={item.id} className="rounded-lg border border-slate-200 bg-white p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-slate-900">
                        {formatMoney(item.ancien_salaire)} -> {formatMoney(item.nouveau_salaire)}
                      </p>
                      <p className="mt-1 text-sm text-slate-500">
                        Effet : {item.mois_effet_nom || '-'} {item.mois_effet_annee || ''}
                      </p>
                    </div>
                    {/*
                      ⚠️ « annulee » DOIT ÊTRE TRAITÉ, SINON IL S'AFFICHE « En attente ».
                      Ce bloc retombait sur « En attente » pour tout ce qui n'était ni
                      approuvé ni refusé. Depuis le 05/10/2026, le Centre de Support
                      pose « annulee » sur les demandes JUMELLES — un même geste demandé
                      plusieurs fois : décider de l'une ferme les autres. Sans cette
                      branche, une demande de modification de salaire fermée se lirait
                      comme une demande qui court encore.
                    */}
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${
                      item.statut === 'approuvee'
                        ? 'bg-emerald-50 text-emerald-700'
                        : item.statut === 'refusee'
                          ? 'bg-red-50 text-red-700'
                          : item.statut === 'annulee'
                            ? 'bg-gray-50 text-gray-600'
                            : 'bg-amber-50 text-amber-700'
                    }`}>
                      {item.statut === 'approuvee'
                        ? 'Approuvee'
                        : item.statut === 'refusee'
                          ? 'Refusee'
                          : item.statut === 'annulee'
                            ? 'Annulee'
                            : 'En attente'}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
                    <div className="rounded-md bg-slate-50 p-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <Clock size={14} />
                        Demande
                      </div>
                      <p className="mt-1 font-semibold text-slate-800">
                        {[item.demandeur_prenom, item.demandeur_nom].filter(Boolean).join(' ') || '-'}
                      </p>
                      <p className="text-xs text-slate-500">{formatDateTime(item.date_demande)}</p>
                    </div>
                    <div className="rounded-md bg-slate-50 p-3">
                      <div className="flex items-center gap-2 text-slate-500">
                        <CheckCircle size={14} />
                        Validation
                      </div>
                      <p className="mt-1 font-semibold text-slate-800">
                        {[item.validateur_prenom, item.validateur_nom].filter(Boolean).join(' ') || '-'}
                      </p>
                      <p className="text-xs text-slate-500">{formatDateTime(item.date_validation)}</p>
                    </div>
                  </div>

                  {item.motif && (
                    <div className="mt-3 rounded-md border border-amber-100 bg-amber-50 p-3 text-sm text-amber-800">
                      {item.motif}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
